import type { Asset, AssetState } from '~/types/asset'
import type { ActionParameter, AssetCapability } from '~/types/capabilities'
import type { AssetConfiguration, ConfigurationValue, ConfigurationValues } from '~/types/asset-configuration'
import { mapLastStates } from '~/helpers/assets-mappers'
import { isVendorCapability, parameterNameKeys } from '~/components/assets/configurations'

const stateValueForParameter = (parameter: ActionParameter, state: AssetState): ConfigurationValue => {
  if (parameter.type === 'number' || parameter.type === 'signedNumber') {
    const numeric = Number(state.stateValue)
    return state.stateValue.trim() !== '' && Number.isFinite(numeric) ? numeric : state.stateValue
  }
  return state.stateValue
}

const stateDate = (state: AssetState): string | null => state.emissionDate || state.dateReceived || null

const isAfter = (isoDate: string, otherIsoDate: string | null): boolean =>
  otherIsoDate === null || new Date(isoDate).getTime() > new Date(otherIsoDate).getTime()

const toConfigurationValue = (value: unknown): ConfigurationValue | undefined =>
  value === null || typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean'
    ? value
    : undefined

interface CurrentValues {
  values: ConfigurationValues
  readAt: string | null
  readAtByKey: Record<string, string>
}

// A vendor configuration has no device state: its current values are the ones stored on the asset, not read from the
// device, so no per-parameter read date is kept.
const storedValues = (asset: Asset, capability: AssetCapability): CurrentValues => {
  const stored = asset.configurations?.[capability.actionKey]
  const values: ConfigurationValues = {}
  for (const nameKey of parameterNameKeys(capability)) {
    const value = toConfigurationValue(stored?.values[nameKey])
    if (value !== undefined) values[nameKey] = value
  }
  return { values, readAt: stored?.setAt || null, readAtByKey: {} }
}

const stateValues = (statesByKey: Map<string, AssetState>, capability: AssetCapability): CurrentValues => {
  const values: ConfigurationValues = {}
  const readAtByKey: Record<string, string> = {}
  let readAt: string | null = null

  for (const entry of capability.capabilities ?? []) {
    for (const parameter of entry.parameters ?? []) {
      const state = parameter.affectedState ? statesByKey.get(parameter.affectedState) : undefined
      if (!state) continue
      values[parameter.nameKey] = stateValueForParameter(parameter, state)
      const date = stateDate(state)
      if (date) readAtByKey[parameter.nameKey] = date
      if (date && isAfter(date, readAt)) readAt = date
    }
  }
  return { values, readAt, readAtByKey }
}

export const buildAssetConfigurations = (asset: Asset, capabilities: AssetCapability[]): AssetConfiguration[] => {
  const statesByKey = new Map(mapLastStates(asset).map((state) => [state.stateKey, state]))
  const preset = asset.configurationPreset
  const [group] = preset?.groups ?? []
  const syncEntries = asset.configurationSync
    ? new Map(asset.configurationSync.entries.map((entry) => [entry.actionKey, entry]))
    : null

  return capabilities.map((capability) => {
    const { values, readAt, readAtByKey } = isVendorCapability(capability)
      ? storedValues(asset, capability)
      : stateValues(statesByKey, capability)

    const configuration: AssetConfiguration = { actionKey: capability.actionKey, values, readAt, readAtByKey }
    const presetValues = preset?.values[capability.actionKey]
    if (preset && group && presetValues) {
      configuration.preset = {
        id: preset.id,
        name: preset.name,
        groupId: group.id,
        groupName: group.name,
        values: presetValues
      }
      if (syncEntries) configuration.sync = syncEntries.get(capability.actionKey) ?? null
    }
    return configuration
  })
}
